// @ts-nocheck
import { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { MobileFormSheet } from '../ui/MobileFormSheet';

type Client = { id: string; name: string };

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated?: (caseId: string) => void;
};

const AREAS = [
  'Civil',
  'Trabalhista',
  'Previdenciário',
  'Criminal',
  'Família',
  'Tributário',
  'Empresarial',
  'Administrativo',
  'Consumidor',
  'Ambiental',
];

export const MobileCaseQuickForm = ({ open, onOpenChange, onCreated }: Props) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [clients, setClients] = useState<Client[]>([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    title: '',
    case_number_cnj: '',
    case_type: 'judicial' as 'judicial' | 'administrative',
    area_of_law: '',
    priority: 'media' as 'baixa' | 'media' | 'alta' | 'critico',
    client_id: '' as string,
    description: '',
  });

  useEffect(() => {
    if (!open || !user) return;
    const loadClients = async () => {
      const { data } = await supabase
        .from('clients')
        .select('id, name')
        .eq('user_id', user.id)
        .order('name');
      setClients(data || []);
    };
    loadClients();
  }, [open, user]);

  useEffect(() => {
    if (open) {
      setForm({
        title: '',
        case_number_cnj: '',
        case_type: 'judicial',
        area_of_law: '',
        priority: 'media',
        client_id: '',
        description: '',
      });
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      toast({ title: 'Informe o título', variant: 'destructive' });
      return;
    }
    if (!user) return;
    setSaving(true);
    try {
      const payload: any = {
        user_id: user.id,
        title: form.title.trim(),
        case_number: form.case_number_cnj.trim() || null,
        case_number_cnj: form.case_number_cnj.trim() || null,
        // case_type no seu banco representa tipo/área da causa (Civil, Criminal etc),
        // não a natureza "administrativo x judicial".
        case_type: form.area_of_law || 'Civil',
        area_of_law: form.area_of_law || null,
        priority: form.priority,
        risk_classification: 'medio',
        status: 'em_andamento',
        client_id: form.client_id || null,
        description: form.description.trim() || null,
        archived: false,
      };
      const payloadWithAdminFlag = {
        ...payload,
        // Mantido apenas para compatibilidade opcional em bancos que possuam a coluna.
        is_administrative_process: false,
      };
      let { data, error } = await supabase
        .from('cases')
        .insert(payloadWithAdminFlag)
        .select('id')
        .single();

      // Compatibilidade com bancos sem a coluna is_administrative_process.
      if (error && /is_administrative_process/i.test(error.message || '')) {
        const fallback = await supabase
          .from('cases')
          .insert(payload)
          .select('id')
          .single();
        data = fallback.data;
        error = fallback.error;
      }
      if (error) throw error;

      toast({ title: 'Processo criado', description: form.title });
      onOpenChange(false);
      onCreated?.(data.id);
    } catch (err: any) {
      toast({
        title: 'Erro ao salvar',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <MobileFormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Novo processo"
      description="Cadastro rápido. Edite os detalhes depois no desktop."
      footer={
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1 h-12"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            className="flex-1 h-12"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Salvando...' : 'Criar'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <Label htmlFor="case-title" className="text-xs font-semibold">
            Título *
          </Label>
          <Input
            id="case-title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Ex.: João X INSS - Aposentadoria"
            className="h-12 mt-1"
          />
        </div>

        <div>
          <Label className="text-xs font-semibold">Tipo</Label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {[
              { id: 'judicial', label: 'Judicial' },
              { id: 'administrative', label: 'Administrativo' },
            ].map((t: any) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setForm({ ...form, case_type: t.id })}
                className={`h-11 rounded-lg border text-sm font-medium transition-colors ${
                  form.case_type === t.id
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card border-border'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label htmlFor="case-number" className="text-xs font-semibold">
            Nº do processo (CNJ ou interno)
          </Label>
          <Input
            id="case-number"
            value={form.case_number_cnj}
            onChange={(e) =>
              setForm({ ...form, case_number_cnj: e.target.value })
            }
            placeholder="0000000-00.0000.0.00.0000"
            className="h-12 mt-1 font-mono"
          />
        </div>

        <div>
          <Label className="text-xs font-semibold">Cliente</Label>
          <Select
            value={form.client_id}
            onValueChange={(v) => setForm({ ...form, client_id: v })}
          >
            <SelectTrigger className="h-12 mt-1">
              <SelectValue placeholder="Selecione (opcional)" />
            </SelectTrigger>
            <SelectContent>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs font-semibold">Área do direito</Label>
          <Select
            value={form.area_of_law}
            onValueChange={(v) => setForm({ ...form, area_of_law: v })}
          >
            <SelectTrigger className="h-12 mt-1">
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              {AREAS.map((a) => (
                <SelectItem key={a} value={a}>
                  {a}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs font-semibold">Prioridade</Label>
          <div className="grid grid-cols-4 gap-1.5 mt-1">
            {[
              { id: 'baixa', label: 'Baixa' },
              { id: 'media', label: 'Média' },
              { id: 'alta', label: 'Alta' },
              { id: 'critico', label: 'Crítica' },
            ].map((p: any) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setForm({ ...form, priority: p.id })}
                className={`h-10 rounded-lg border text-xs font-medium transition-colors ${
                  form.priority === p.id
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card border-border'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label htmlFor="case-desc" className="text-xs font-semibold">
            Descrição
          </Label>
          <Textarea
            id="case-desc"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Resumo, estratégia, observações..."
            className="mt-1 min-h-[80px]"
          />
        </div>
      </div>
    </MobileFormSheet>
  );
};

export default MobileCaseQuickForm;
